import { Menu, StudentMenu, MealSlot, MealLock } from '@/plugins/api'
import moment from 'moment'
import { get } from 'lodash'
import { DEFAULT_SLOT_TITLE } from '@/modules/mealConfig/mealConfigConstants'

// serveDays → các thứ trong tuần (1=T2..6=T7), khớp BE
const RANGE = { 'mon-fri': [1, 2, 3, 4, 5], 'mon-sat': [1, 2, 3, 4, 5, 6] }
const WEEKDAY_NUM = { mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 }
const serveDaysToNums = sd => RANGE[sd] || (WEEKDAY_NUM[sd] ? [WEEKDAY_NUM[sd]] : [])
const TIME_ORDER = { morning: 0, lunch: 1, afternoon: 2, evening: 3 }

// Thứ tự hiển thị T2→CN: { weekday (0=CN..6=T7), offset từ thứ 2 }
const WEEK_ORDER = [
  { wd: 1, off: 0 },
  { wd: 2, off: 1 },
  { wd: 3, off: 2 },
  { wd: 4, off: 3 },
  { wd: 5, off: 4 },
  { wd: 6, off: 5 },
  { wd: 0, off: 6 }
]

const slotIdOf = ref => (ref && (ref.id || ref._id || ref)) || null

const timeRank = v => (TIME_ORDER[v] != null ? TIME_ORDER[v] : 99)
const sortFixed = (a, b) => {
  const t = timeRank(a.timeOfDay) - timeRank(b.timeOfDay)
  if (t) return t
  if ((a.displayOrder || 1) !== (b.displayOrder || 1)) return (a.displayOrder || 1) - (b.displayOrder || 1)
  const titleCmp = String(a.title || '').localeCompare(String(b.title || ''), 'vi')
  if (titleCmp) return titleCmp
  return new Date(a.createdAt) - new Date(b.createdAt)
}

export default {
  namespaced: true,
  state: {
    loading: false,
    date: moment().toISOString(),
    rows: [],
    defaultSlotId: null
  },
  actions: {
    async fetchData({ commit, state }) {
      state.loading = true
      try {
        const startWeek = moment(state.date).startOf('week').toISOString()
        const endWeek = moment(state.date).endOf('week').toISOString()
        const weekStart = moment(state.date).startOf('isoWeek') // thứ 2

        const [slots, menus, counts, locks] = await Promise.all([
          MealSlot.fetch({ isActive: true, _limit: -1 }),
          Menu.fetch({ date_gte: startWeek, date_lte: endWeek, _limit: -1 }),
          StudentMenu.getStudentMenuCountReportByWeek({ startDate: startWeek, endDate: endWeek }),
          MealLock.fetch({ _limit: -1 })
        ])
        const countList = get(counts, 'data', counts) || []

        const rows = []
        WEEK_ORDER.forEach(({ wd, off }) => {
          const dayMoment = weekStart.clone().add(off, 'day')
          const dateStr = dayMoment.format('YYYY-MM-DD')
          const dateISO = dayMoment.clone().startOf('day').toISOString()

          const fixed = slots
            .filter(s => s.type === 'fixed' && serveDaysToNums(s.serveDays).includes(wd))
            .sort(sortFixed)
          const supplementary = slots
            .filter(s => s.type === 'supplementary' && s.serveDate && moment(s.serveDate).format('YYYY-MM-DD') === dateStr)
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
          const daySlots = [...fixed, ...supplementary]

          if (!daySlots.length) {
            rows.push({ isDay: true, day: wd, date: dateISO, empty: true })
            return
          }

          daySlots.forEach((slot, idx) => {
            const slotId = String(slot.id)
            const menu =
              menus.find(m => String(m.day) === String(wd) && slotIdOf(m.meal_slot) === slotId) ||
              // bữa mặc định: menu cũ (chưa gắn meal_slot)
              (state.defaultSlotId === slotId
                ? menus.find(m => String(m.day) === String(wd) && !m.meal_slot)
                : null)
            const cnt = countList.find(c => String(c.day) === String(wd) && String(c.mealSlot) === slotId) || {}
            const slotLocks = locks.filter(
              l =>
                slotIdOf(l.meal_slot) === slotId &&
                dateStr >= moment(l.fromDate).format('YYYY-MM-DD') &&
                (!l.toDate || dateStr <= moment(l.toDate).format('YYYY-MM-DD'))
            )

            rows.push({
              isDay: true,
              firstOfDay: idx === 0,
              day: wd,
              date: dateISO,
              slot,
              slotId,
              menu: menu || null,
              menu1Main: get(menu, 'firstMainDishes', []),
              menu1Side: get(menu, 'firstSideDishes', []),
              menu2Main: get(menu, 'secondMainDishes', []),
              menu2Side: get(menu, 'secondSideDishes', []),
              count1: cnt.countMenu1 || 0,
              count2: cnt.countMenu2 || 0,
              locks: slotLocks
            })
          })
        })
        commit('changeState', { rows })
      } finally {
        state.loading = false
      }
    },
    async loadDefaultSlot({ commit, state }) {
      if (state.defaultSlotId) return
      const list = await MealSlot.fetch({ title: DEFAULT_SLOT_TITLE, _limit: 1 })
      if (get(list, 'length')) commit('changeState', { defaultSlotId: String(list[0].id) })
    },
    setDate({ commit }, date) {
      commit('changeState', { date })
    },
    async createMenu(_, params) {
      await Menu.create(params)
    },
    async updateMenu(_, { id, ...params }) {
      await Menu.update(id, params)
    }
  },
  mutations: {},
  getters: {
    startWeek: state => moment(state.date).startOf('week'),
    endWeek: state => moment(state.date).endOf('week')
  }
}
